import { httpDashboardStats } from "api/admin/stats";
import { fetchEddReviews, fetchKybReviews } from "api/admin/kycReviews";
import AdminSidebar from "components/layout/AdminSidebar";
import Container from "components/layout/Container";
import Main from "components/layout/Main";
import Spinner from "components/ui/Spinner";
import Table from "components/ui/Table";
import { currentAdminCountryScope } from "helpers/storage";
import useAdminAccess from "hooks/useAdminAccess";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { adminRoutes } from "routes/routes";
import { requiredAdminPermissionsForPath } from "services/adminAccess";
import currency, { moneyFormat } from "utilities/currency";
import { cryptos } from "utilities/wallets";
import { reviewKey } from "screens/__ADMIN__/kyc/ReviewComponents";

const SECTION_PERMISSIONS = [
  "stats.userMetrics.view",
  "stats.financialBalances.view",
  "users.kyc.view",
  "transactions.withdrawalReviews.view",
];

const Dashboard: React.FC = () => {
  const navigation = useNavigate();
  const { admin, can, canAny } = useAdminAccess();
  const permittedNavigation = (path: string) =>
    canAny(requiredAdminPermissionsForPath(path) ?? [])
      ? () => navigation(path)
      : undefined;
  const dashboard = useQuery(
    [
      "dashboardStats",
      admin?.id,
      currentAdminCountryScope(),
      SECTION_PERMISSIONS.filter(can),
    ],
    httpDashboardStats,
    { enabled: can("stats.dashboard.view"), retry: false },
  );
  const stats = dashboard.data?.stats ?? {};
  const canViewKyc = can("stats.dashboard.view") && can("users.kyc.view");
  const eddQueue = useQuery(
    [...reviewKey("edd", admin?.id), "count"],
    ({ signal }) => fetchEddReviews(1, "submitted", 1, signal),
    { enabled: canViewKyc, retry: false },
  );
  const kybQueue = useQuery(
    [...reviewKey("kyb", admin?.id), "count"],
    ({ signal }) => fetchKybReviews(1, "pending", 1, signal),
    { enabled: canViewKyc, retry: false },
  );
  const showFinancial =
    can("stats.financialBalances.view") && stats.totalBalance !== undefined;
  const showUsers =
    can("stats.userMetrics.view") && stats.totalRegisteredUsers !== undefined;
  const queues = [
    {
      permission: "users.kyc.view",
      label: "Tier 3 EDD Reviews",
      count: eddQueue.isError
        ? "Unavailable"
        : (eddQueue.data?.meta.total ?? "Loading…"),
      path: adminRoutes.eddReviews,
    },
    {
      permission: "users.kyc.view",
      label: "Tier 4 KYB Reviews",
      count: kybQueue.isError
        ? "Unavailable"
        : (kybQueue.data?.meta.total ?? "Loading…"),
      path: adminRoutes.kybReviews,
    },
    {
      permission: "users.kyc.view",
      label: "Pending KYC Escalations",
      count: stats.actionQueue?.pendingKycEscalations,
      path: `${adminRoutes.users}?kycReviewStatus=escalated`,
    },
    {
      permission: "transactions.withdrawalReviews.view",
      label: "Flagged Transactions",
      count: stats.actionQueue?.flaggedTransactions,
      path: adminRoutes.flaggedTransactions,
    },
  ].filter((queue) => can(queue.permission) && queue.count !== undefined);

  const balanceRow = (
    label: string,
    amount: number | string | undefined,
    path: string,
  ) => [
    label,
    amount === undefined
      ? "—"
      : `${currency("ngn")} ${moneyFormat(Number(amount))}`,
    amount === undefined || !stats.rate?.buy.usd
      ? "—"
      : `${currency("usd")} ${moneyFormat(Number(amount) / stats.rate.buy.usd)}`,
    permittedNavigation(path),
  ];

  return (
    <Container>
      <AdminSidebar />
      <Main className="mx-5 mt-8 text-left">
        {dashboard.isLoading ? (
          <Spinner className="text-lg" />
        ) : dashboard.isError ? (
          <p role="alert">
            Unable to load the dashboard. Please refresh to try again.
          </p>
        ) : (
          <>
            {(showFinancial || showUsers) && (
              <div className="flex gap-12">
                {showFinancial && (
                  <div className="w-4/12 p-8 text-white bg-black rounded-lg">
                    <h4 className="mb-2 text-sm font-normal">
                      Total Customer Balance
                    </h4>
                    <p className="text-3xl font-medium">
                      <span className="text-2xl">{currency("ngn")}</span>{" "}
                      {moneyFormat(stats.totalBalance!)}
                    </p>
                  </div>
                )}
                {showUsers && (
                  <>
                    <div className="flex items-center w-3/12 p-8 bg-orange-100 rounded-lg">
                      <i className="mr-3 text-2xl text-orange-300 fa fa-user" />
                      <div>
                        <h4 className="mb-1 text-3xl font-medium">
                          {stats.totalRegisteredUsers}
                        </h4>
                        <p className="text-sm">Registered users</p>
                      </div>
                    </div>
                    <div className="flex items-center w-3/12 p-8 bg-orange-100 rounded-lg">
                      <i className="mr-3 text-2xl text-orange-300 fa fa-user" />
                      <div>
                        <h4 className="mb-1 text-3xl font-medium">
                          {stats.totalActiveUsers}
                        </h4>
                        <p className="text-sm">Active users</p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {showFinancial && (
              <div className="mt-14">
                <h4 className="mb-5 text-lg font-normal">Total Balances</h4>
                <div className="flex gap-10">
                  <Table
                    header={["#", "Naira", "Dollar"]}
                    data={[
                      ...(stats.totalVendorsBalance !== undefined
                        ? [
                            balanceRow(
                              "Merchants",
                              stats.totalVendorsBalance,
                              `${adminRoutes.balances}/merchant`,
                            ),
                          ]
                        : []),
                      balanceRow(
                        "Users",
                        stats.totalUsersBalance,
                        `${adminRoutes.balances}/user`,
                      ),
                      balanceRow(
                        "Naira",
                        stats.usersNairaBalance,
                        `${adminRoutes.balances}/naira`,
                      ),
                      balanceRow(
                        "Crypto",
                        stats.totalCryptoBalances,
                        `${adminRoutes.balances}/crypto`,
                      ),
                      balanceRow(
                        "Recurring",
                        stats.totalRecurring,
                        `${adminRoutes.transactions}/recurring`,
                      ),
                      balanceRow(
                        "BTC",
                        stats.cryptoBalances?.btc ?? 0,
                        `${adminRoutes.balances}/btc`,
                      ),
                    ]}
                  />
                  <Table
                    header={["#", "Naira", "Dollar"]}
                    data={cryptos
                      .filter((crypto) => crypto !== "btc")
                      .map((crypto) =>
                        balanceRow(
                          crypto.toUpperCase(),
                          stats.cryptoBalances?.[crypto] ?? 0,
                          `${adminRoutes.balances}/${crypto}`,
                        ),
                      )}
                  />
                </div>
              </div>
            )}

            {queues.length > 0 && (
              <div className="mt-14">
                <h4 className="mb-5 text-lg font-normal">Action Queue</h4>
                <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                  {queues.map((queue) => (
                    <button
                      key={queue.path}
                      className="p-5 text-left transition bg-white border border-gray-200 rounded shadow-sm enabled:hover:border-orange-300 enabled:hover:bg-orange-50"
                      disabled={!permittedNavigation(queue.path)}
                      onClick={permittedNavigation(queue.path)}
                      type="button"
                    >
                      <p className="text-sm text-gray-500">{queue.label}</p>
                      <p className="mt-3 text-3xl font-medium">{queue.count}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {!showFinancial && !showUsers && queues.length === 0 && (
              <p className="text-gray-500">
                No dashboard sections are available for your account. Your
                available tools are in the menu.
              </p>
            )}
          </>
        )}
      </Main>
    </Container>
  );
};
export default Dashboard;
